import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import api from "../../utils/api";
import { toast } from "react-toastify";
import {
  loadingAlert,
  closeAlert,
  successAlert,
  errorAlert,
  confirmAlert,
} from "../../utils/swalConfig";

const NotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    message: "",
    type: "info",
  });
  const [editingId, setEditingId] = useState(null);

  const fetchNotifications = async () => {
    try {
      loadingAlert("Loading notifications...");
      const token = localStorage.getItem("jwt");
      const response = await api.get("/notifications/active", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data.data.notifications);
      closeAlert();
    } catch (err) {
      closeAlert();
      errorAlert("Error!", "Failed to fetch notifications");
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleSubmit = async () => {
    try {
      loadingAlert(
        editingId ? "Updating notification..." : "Creating notification..."
      );
      const token = localStorage.getItem("jwt");

      if (editingId) {
        await api.patch(`/notifications/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.post("/notifications", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      closeAlert();
      successAlert(
        "Success!",
        `Notification ${editingId ? "updated" : "created"} successfully`
      );
      setOpenDialog(false);
      setFormData({ message: "", type: "info" });
      setEditingId(null);
      fetchNotifications();
    } catch (err) {
      closeAlert();
      errorAlert("Error!", err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert(
      "Delete Notification",
      "Are you sure you want to delete this notification?",
      "warning"
    );

    if (result.isConfirmed) {
      try {
        loadingAlert("Deleting notification...");
        const token = localStorage.getItem("jwt");
        await api.delete(`/notifications/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        closeAlert();
        successAlert("Success!", "Notification deleted successfully");
        fetchNotifications();
      } catch (err) {
        closeAlert();
        errorAlert("Error!", "Failed to delete notification");
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Notification Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Notification
        </Button>
      </Box>

      <Card>
        <CardContent>
          <List>
            {notifications.map((notification) => (
              <ListItem key={notification._id} divider>
                <ListItemText
                  primary={notification.message}
                  secondary={`Type: ${notification.type}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingId(notification._id);
                      setFormData({
                        message: notification.message,
                        type: notification.type,
                      });
                      setOpenDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(notification._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId ? "Edit Notification" : "Create Notification"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Message"
            value={formData.message}
            onChange={(e) =>
              setFormData({ ...formData, message: e.target.value })
            }
            sx={{ mt: 2, mb: 2 }}
            multiline
            rows={3}
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              label="Type"
            >
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationManagement;
